import { useEffect, useState } from "react";
import { useStore } from "../store.ts";
import { audioUrl, fetchChart, fetchLibrary, deleteTrack } from "../api.ts";

/**
 * The catalogue of charted tracks. Public — playing is open to anyone.
 * Adding a track is gated; see the Add screen and the server's auth.ts.
 */
export function Library() {
  const store = useStore();
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load(refresh = false) {
    useStore.getState().set({ loadingLibrary: true, libraryError: null });
    try {
      const entries = await fetchLibrary(refresh);
      useStore.getState().set({ entries, loadingLibrary: false });
    } catch (e) {
      useStore.getState().set({
        loadingLibrary: false,
        libraryError: e instanceof Error ? e.message : String(e),
      });
    }
  }

  /**
   * Load a track: chart JSON, then the audio, decoded for sample-accurate
   * playback. Decoding is what forces the audio through our own origin —
   * decodeAudioData needs a CORS-clean response.
   */
  async function play(hash: string) {
    setBusy(hash);
    const set = useStore.getState().set;
    set({ loadingTrack: true, trackError: null });
    const ctx = new AudioContext();
    try {
      const chart = await fetchChart(hash);
      const res = await fetch(audioUrl(hash));
      if (!res.ok) throw new Error(`Could not load audio (${res.status}).`);
      const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
      set({ chart, buffer, loadingTrack: false, screen: "calibrate" });
    } catch (e) {
      set({
        loadingTrack: false,
        trackError:
          e instanceof Error
            ? `${e.message} The track may have been removed, or the browser can't decode this format.`
            : String(e),
      });
    } finally {
      await ctx.close();
      setBusy(null);
    }
  }

  async function remove(hash: string, label: string) {
    if (!confirm(`Remove "${label}" from the library? This deletes the chart and the audio.`)) {
      return;
    }
    setBusy(hash);
    const ok = await deleteTrack(hash);
    setBusy(null);
    if (ok) void load(true);
    else alert("Delete failed — check your passphrase.");
  }

  return (
    <>
      <div className="panel">
        <div className="row">
          <div>
            <h2>Library</h2>
            <p className="sub" style={{ margin: 0 }}>
              {store.entries.length} charted{" "}
              {store.entries.length === 1 ? "track" : "tracks"}. Playing is open to
              everyone.
            </p>
          </div>
          <div className="spacer" />
          <button onClick={() => void load(true)} disabled={store.loadingLibrary}>
            {store.loadingLibrary ? "Loading…" : "Refresh"}
          </button>
          <button
            className="primary"
            onClick={() => useStore.getState().set({ screen: "add" })}
          >
            Add a track
          </button>
        </div>

        {store.libraryError && <p className="note bad">{store.libraryError}</p>}
        {store.trackError && <p className="note bad">{store.trackError}</p>}

        {!store.loadingLibrary && store.entries.length === 0 && !store.libraryError && (
          <p className="note">
            Nothing charted yet. Add a track to get started — you'll need the
            contribution passphrase.
          </p>
        )}

        <div className="rounds" style={{ marginTop: 14 }}>
          {store.entries.map((e) => (
            <div className="round-btn" key={e.fileHash} style={{ cursor: "default" }}>
              {e.coverArtUrl && (
                <img
                  src={e.coverArtUrl}
                  alt=""
                  width={44}
                  height={44}
                  style={{ borderRadius: 6, objectFit: "cover" }}
                  onError={(ev) => ((ev.target as HTMLImageElement).style.display = "none")}
                />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {e.title || "(untitled)"}
                  <span className="meta"> — {e.artist || "unknown"}</span>
                </div>
                <div className="meta">
                  {Math.round(e.duration)}s · {e.words} words · {e.playableSections}{" "}
                  playable {e.playableSections === 1 ? "section" : "sections"}
                  {e.source === "lrc-only" && " · interpolated timings"}
                  {!e.hasAudio && " · audio missing"}
                </div>
                {e.flags.length > 0 && (
                  <div className="meta" style={{ color: "var(--warn)" }}>
                    {e.flags.join(", ")}
                  </div>
                )}
              </div>
              <div className="spacer" />
              {store.authed && (
                <button
                  onClick={() => void remove(e.fileHash, `${e.artist} — ${e.title}`)}
                  disabled={busy !== null}
                  title="Remove from library"
                >
                  ✕
                </button>
              )}
              <button
                className="primary"
                disabled={busy !== null || !e.hasAudio || e.playableSections === 0}
                onClick={() => void play(e.fileHash)}
              >
                {busy === e.fileHash ? "Loading…" : "Play"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <p className="sub">
        Storage: {store.storage}. Uploads are{" "}
        {store.uploadsGated ? "passphrase-gated" : "open"}.
      </p>
    </>
  );
}
